import React from "react";
import { List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import LocalHospitalRounded from "@mui/icons-material/LocalHospitalRounded";
import QuizRounded from "@mui/icons-material/QuizRounded";
import FlashOnRounded from "@mui/icons-material/FlashOnRounded";
import CalculateRounded from "@mui/icons-material/CalculateRounded";
import WarningRounded from "@mui/icons-material/WarningRounded";
import ScienceRounded from "@mui/icons-material/ScienceRounded";
import LibraryBooksRounded from "@mui/icons-material/LibraryBooksRounded";
import PolicyRounded from "@mui/icons-material/PolicyRounded";
import PresentToAllRounded from "@mui/icons-material/PresentToAllRounded";
import MedicalInformationRounded from "@mui/icons-material/MedicalInformationRounded";
import PsychologyRounded from "@mui/icons-material/PsychologyRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import HealthAndSafetyRounded from "@mui/icons-material/HealthAndSafetyRounded";

interface ChatSuggestion {
  buttonText: string;
  fullMessage: string;
  icon: React.ReactNode;
}

interface ChatSuggestionsProps {
  assistantType: string;
  onSuggestionClick: (message: string) => void;
}

const getSuggestions = (assistantType: string): ChatSuggestion[] => {
  const suggestions = {
    general: [
      {
        buttonText: "Help me make sense of symptoms I'm having",
        fullMessage:
          "I have symptoms I'd like you to analyze and provide guidance on what they might mean.",
        icon: <SearchRounded />,
      },
      {
        buttonText: "Work out what kind of care I need",
        fullMessage:
          "Help me determine what type of healthcare provider or care setting is appropriate for my situation.",
        icon: <LocalHospitalRounded />,
      },
      {
        buttonText: "Explain a condition and how it's treated",
        fullMessage:
          "I have a medical condition I'd like you to explain, including treatment options.",
        icon: <QuizRounded />,
      },
    ],
    brainflash: [
      {
        buttonText: "Calculate a medication dose quickly",
        fullMessage:
          "I need quick medication dosing calculations and recommendations.",
        icon: <CalculateRounded />,
      },
      {
        buttonText: "Check contraindications and interactions for a drug",
        fullMessage:
          "What are the key contraindications and interactions for a specific medication?",
        icon: <WarningRounded />,
      },
      {
        buttonText: "Walk me through an emergency protocol",
        fullMessage:
          "I need rapid guidance on emergency protocols and immediate management steps.",
        icon: <FlashOnRounded />,
      },
    ],
    "deep-dive": [
      {
        buttonText: "Explore the pathophysiology of a condition",
        fullMessage:
          "I want to explore the detailed pathophysiology and mechanisms of a condition.",
        icon: <ScienceRounded />,
      },
      {
        buttonText: "Review the evidence behind a treatment",
        fullMessage:
          "What does current research and evidence say about a treatment or clinical approach?",
        icon: <LibraryBooksRounded />,
      },
      {
        buttonText: "Summarize current guidelines for managing a condition",
        fullMessage:
          "What are the current evidence-based guidelines for managing this condition?",
        icon: <PolicyRounded />,
      },
    ],
    "peer-review": [
      {
        buttonText: "Present a clinical case for discussion",
        fullMessage:
          "I'd like to present a clinical case for collaborative discussion and input.",
        icon: <PresentToAllRounded />,
      },
      {
        buttonText: "Build a management plan for a patient",
        fullMessage:
          "I have a patient case where I'd like to develop a comprehensive management plan.",
        icon: <MedicalInformationRounded />,
      },
      {
        buttonText: "Walk through the clinical reasoning on a complex case",
        fullMessage:
          "Walk me through the clinical reasoning process for a complex case.",
        icon: <PsychologyRounded />,
      },
    ],
    "basic-medical-library": [
      {
        buttonText: "Help me understand a condition I've been diagnosed with",
        fullMessage:
          "Help me understand what a medical condition is and how it affects me.",
        icon: <HelpOutlineRounded />,
      },
      {
        buttonText: "Explain what my symptoms could mean",
        fullMessage:
          "Can you explain what different symptoms mean and why they happen?",
        icon: <HealthAndSafetyRounded />,
      },
      {
        buttonText: "Tell me what a medical test is for and what to expect",
        fullMessage: "What does this medical test do and why might I need it?",
        icon: <ScienceRounded />,
      },
    ],
  };

  return suggestions[assistantType] || [];
};

const ChatSuggestions: React.FC<ChatSuggestionsProps> = ({
  assistantType,
  onSuggestionClick,
}) => {
  const suggestions = getSuggestions(assistantType);

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  // ChatGPT-style starters: a quiet list of links under the composer, one per row.
  return (
    <List dense disablePadding sx={{ mt: 1.5, width: "100%" }}>
      {suggestions.map((suggestion, index) => (
        <ListItemButton
          key={index}
          onClick={() => onSuggestionClick(suggestion.fullMessage)}
          sx={{ borderRadius: 2, py: 1.25 }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>{suggestion.icon}</ListItemIcon>
          <ListItemText primary={suggestion.buttonText} primaryTypographyProps={{ color: "text.secondary" }} />
        </ListItemButton>
      ))}
    </List>
  );
};

export default ChatSuggestions;
